import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { adminClient, type AdminCreditEntry, type AdminOrgBilling } from '@/api/adminClient';
import { CREDIT_PAGE_SIZE } from '@/lib/format';
import { latestGrant } from '@/lib/orgMoney';

/** Billing and the newest credit grant, loaded once for the Money page and its panels. */
export function useOrgMoney(orgId: string) {
  const [billing, setBilling] = useState<AdminOrgBilling | undefined>();
  const [lastGrant, setLastGrant] = useState<AdminCreditEntry | undefined>();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | undefined>();

  const reload = useCallback(() => {
    setLoading(true);
    setLoadError(undefined);
    void adminClient
      .listOrgCreditEntries(orgId, { limit: CREDIT_PAGE_SIZE, offset: 0 })
      .then((result) => setLastGrant(latestGrant(result.items)))
      .catch(() => undefined);
    adminClient
      .getOrgBilling(orgId)
      .then(setBilling)
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : 'Failed to load plan and billing';
        setLoadError(message);
        toast.error(message);
      })
      .finally(() => setLoading(false));
  }, [orgId]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { billing, setBilling, lastGrant, loading, loadError, reload };
}
